// src/layout/builders/ERP/PettyCashTab.jsx
import React, { useEffect, useState } from "react";
import useERPStore from "../../../store/builders/useERPStore";
import useAuthStore from "../../../store/useAuthStore";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import {
  Plus,
  XCircle,
  Clock,
  Wallet,
  X,
  Trash2,
  Receipt,
  History,
  PieChart,
  Activity,
  CheckCircle2,
  Eye,
  ArrowLeft
} from "lucide-react";

const emptyRequestForm = { project_id: "", amount: "", purpose: "", notes: "" };
const emptyAllocateForm = {
  project_id: "",
  assigned_amount: "",
  allocation_date: new Date().toISOString().split("T")[0],
  reason: "",
};

const StatusBadge = ({ status }) => {
  const cfg = {
    pending: { cls: "bg-amber-50 text-amber-600 border-amber-100", icon: <Clock size={12} />, label: "Pending Approval" },
    approved: { cls: "bg-emerald-50 text-emerald-600 border-emerald-100", icon: <CheckCircle2 size={12} />, label: "Approved" },
    rejected: { cls: "bg-rose-50 text-rose-600 border-rose-100", icon: <XCircle size={12} />, label: "Rejected" },
  };
  const c = cfg[status] || cfg.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider border ${c.cls}`}>
      {c.icon}{c.label}
    </span>
  );
};

const PettyCashTab = () => {
  const {
    pettyCash,
    pettyCashRequests,
    pettyCashExpenses,
    pettyCashAllocations,
    isLoading,
    fetchPettyCash,
    upsertPettyCash,
    fetchPettyCashRequests,
    createPettyCashRequest,
    updatePettyCashRequestStatus,
    fetchPettyCashExpenses,
    deletePettyCashExpense,
    fetchPettyCashAllocations,
  } = useERPStore();
  const { user } = useAuthStore();
  const [projects, setProjects] = useState([]);
  const [activeSection, setActiveSection] = useState("overview");
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState(emptyRequestForm);
  const [approveModalReq, setApproveModalReq] = useState(null);
  const [rejectModalReq, setRejectModalReq] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedProject, setSelectedProject] = useState("");
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [allocateForm, setAllocateForm] = useState(emptyAllocateForm);
  const [historyProject, setHistoryProject] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchPettyCash();
    fetchPettyCashRequests();
    if (user?.id) {
      axiosInstancev1.get(`/projects?builderId=${user.id}&status=active&limit=1000&page=1`)
        .then(r => setProjects(r.data?.projects || [])).catch(() => { });
    }
  }, [fetchPettyCash, fetchPettyCashRequests, user?.id]);

  useEffect(() => {
    if (selectedProject) fetchPettyCashExpenses(selectedProject);
  }, [selectedProject, fetchPettyCashExpenses]);

  const pendingRequests = pettyCashRequests.filter(r => r.status === "pending");

  const handleRequestSave = async (e) => {
    e.preventDefault(); setSaving(true);
    const ok = await createPettyCashRequest(requestForm);
    setSaving(false);
    if (ok) setShowRequestModal(false);
  };

  const handleAllocateSave = async (e) => {
    e.preventDefault();
    if (!allocateForm.reason?.trim()) return;
    setSaving(true);
    const ok = await upsertPettyCash(allocateForm);
    setSaving(false);
    if (ok) {
      setShowAllocateModal(false);
      setAllocateForm(emptyAllocateForm);
    }
  };

  const openAllocationHistory = async (pc) => {
    setHistoryProject(pc);
    setHistoryLoading(true);
    await fetchPettyCashAllocations(pc.project_id);
    setHistoryLoading(false);
  };

  const handleApproveClick = (req) => {
    setApproveModalReq(req);
    setEditAmount(req.amount);
  };

  const submitApprove = async (e) => {
    e.preventDefault();
    setSaving(true);
    let approveNotes = null;
    if (parseFloat(editAmount) !== parseFloat(approveModalReq.amount)) {
      approveNotes = `[Edited] Amount changed from ₹${parseFloat(approveModalReq.amount).toLocaleString()} to ₹${parseFloat(editAmount).toLocaleString()}`;
    }
    await updatePettyCashRequestStatus(approveModalReq.id, "approved", approveModalReq.project_id, editAmount, approveNotes);
    setSaving(false);
    setApproveModalReq(null);
  };

  const handleRejectClick = (req) => {
    setRejectModalReq(req);
    setRejectNotes("");
  };

  const submitReject = async (e) => {
    e.preventDefault();
    if (!rejectNotes.trim()) return;
    setSaving(true);
    await updatePettyCashRequestStatus(rejectModalReq.id, "rejected", rejectModalReq.project_id, 0, rejectNotes);
    setSaving(false);
    setRejectModalReq(null);
  };

  const SECTIONS = [
    { key: "overview", label: "Financial Health", icon: PieChart },
    { key: "requests", label: "Cash Request from Project", icon: Receipt, badge: pendingRequests.length },
    { key: "expenses", label: "Audit Ledger", icon: History },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4 bg-white p-4 rounded-xl border border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600 border border-amber-100">
            <Wallet size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-800">Petty Cash Ledger</h2>
            <p className="text-xs text-gray-500 font-medium">On-site Expense Balance</p>
          </div>
        </div>
        {!historyProject && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setShowAllocateModal(true)}
              className="flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-700 transition shadow-sm">
              <Plus size={16} /> Allocate Amount
            </button>
          </div>
        )}
      </div>

      {historyProject ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden animate-in fade-in slide-in-from-right-4 duration-300">
          <div className="flex items-center gap-3 p-4 sm:p-5 border-b bg-gray-50/80">
            <button
              type="button"
              onClick={() => setHistoryProject(null)}
              className="p-2 text-gray-500 hover:text-blue-600 transition-colors bg-white border border-gray-200 rounded-lg shadow-sm shrink-0"
              title="Back to allocations"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 font-medium">Allocate Amount History</p>
              <h3 className="text-lg font-bold text-gray-800 truncate">
                {historyProject.project_name}
              </h3>
            </div>
          </div>

          {historyLoading ? (
            <div className="py-16 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-emerald-600 mx-auto"></div>
            </div>
          ) : pettyCashAllocations.length === 0 ? (
            <div className="py-16 text-center flex flex-col items-center gap-3">
              <History size={32} className="text-gray-200" />
              <p className="text-gray-400 font-medium text-sm">No allocation history for this project.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Reason</th>
                    <th className="text-right px-4 py-3">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pettyCashAllocations.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50/30 transition-colors">
                      <td className="px-4 py-4 text-gray-600 font-medium whitespace-nowrap">
                        {new Date(row.allocation_date).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-bold text-gray-800 whitespace-pre-wrap">{row.reason}</span>
                      </td>
                      <td className="px-4 py-4 text-right font-bold text-emerald-600 tabular-nums">
                        ₹{parseFloat(row.amount || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 font-bold border-t border-gray-200">
                  <tr>
                    <td colSpan={2} className="px-4 py-4 text-gray-500 text-[10px] uppercase">Total Allocated</td>
                    <td className="px-4 py-4 text-right text-base text-emerald-600 tabular-nums">
                      ₹{pettyCashAllocations.reduce((s, r) => s + parseFloat(r.amount || 0), 0).toLocaleString("en-IN")}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      ) : (
        <>
      {/* Navigation Pills */}
      <div className="flex gap-2 p-1 bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {SECTIONS.map(s => {
          const Icon = s.icon;
          const active = activeSection === s.key;
          return (
            <button key={s.key} onClick={() => setActiveSection(s.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${active
                ? "bg-blue-600 text-white shadow-sm"
                : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                }`}>
              <Icon size={16} />
              {s.label}
              {s.badge > 0 && (
                <span className={`ml-1.5 flex items-center justify-center min-w-[18px] h-4.5 px-1 rounded-full text-[9px] font-bold ${active ? "bg-white text-blue-600" : "bg-amber-500 text-white"}`}>
                  {s.badge}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-100 border-t-amber-600"></div>
          <p className="text-sm font-medium text-slate-500">Syncing transaction diaries...</p>
        </div>
      ) : (
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          {activeSection === "overview" && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800 text-base">Cash Allocation (Site Wise)</h3>
              </div>
              {pettyCash.length === 0 ? (
                <div className="p-16 text-center text-gray-500 font-medium">No projects assigned petty cash.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                        <th className="text-left px-4 py-3">Project</th>
                        <th className="text-right px-4 py-3">Assigned</th>
                        <th className="text-right px-4 py-3">Used</th>
                        <th className="text-right px-4 py-3">Balance</th>
                        <th className="text-center px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pettyCash.map((pc) => {
                        const remaining = parseFloat(pc.remaining_amount || 0);
                        return (
                          <tr key={pc.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4">
                              <div className="font-bold text-gray-700">{pc.project_name}</div>
                              <div className="text-[10px] text-gray-400 font-bold uppercase">ID: {pc.project_id}</div>
                            </td>
                            <td className="px-4 py-4 text-right font-bold text-gray-600 tabular-nums">₹{parseFloat(pc.assigned_amount).toLocaleString()}</td>
                            <td className="px-4 py-4 text-right font-bold text-red-500 tabular-nums">₹{parseFloat(pc.used_amount).toLocaleString()}</td>
                            <td className="px-4 py-4 text-right">
                              <span className={`text-base font-bold tabular-nums ${remaining >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                                ₹{remaining.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex justify-center">
                                <button
                                  type="button"
                                  onClick={() => openAllocationHistory(pc)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-all font-semibold text-xs border border-blue-100"
                                >
                                  <Eye size={14} /> View
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeSection === "requests" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pettyCashRequests.length === 0 ? (
                <div className="col-span-full py-16 text-center text-gray-500 font-medium">No pending requests.</div>
              ) : pettyCashRequests.map((req) => (
                <div key={req.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-800 text-lg">₹{parseFloat(req.amount).toLocaleString()}</span>
                        <StatusBadge status={req.status} />
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <p className="text-gray-700 text-sm font-medium">{req.purpose}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mt-1">{new Date(req.created_at).toLocaleDateString()}</p>
                        {req.notes && (
                          <div className="mt-2 text-xs text-gray-600 border-t border-gray-200 pt-2 italic whitespace-pre-line">
                            {req.notes.replace(/\nReason: /g, '')}
                          </div>
                        )}
                      </div>
                      <div className="text-xs font-bold text-blue-600 flex items-center gap-1.5">
                        <Activity size={12} /> {req.project_name}
                      </div>
                    </div>
                    {req.status === "pending" && (
                      <div className="flex flex-col gap-2">
                        <button onClick={() => handleApproveClick(req)} className="p-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-lg transition-all" title="Approve"><CheckCircle2 size={18} /></button>
                        <button onClick={() => handleRejectClick(req)} className="p-2 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-all" title="Reject"><X size={18} /></button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeSection === "expenses" && (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3 shadow-sm">
                <span className="text-base sm:text-lg font-bold text-gray-900 px-1">Select Project</span>
                <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
                  className="w-full sm:w-64 max-w-full bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-800 outline-none focus:border-blue-500 cursor-pointer">
                  <option value="">Select Project...</option>
                  {projects.map(pr => <option key={pr.id} value={pr.id}>{pr.project_name}</option>)}
                </select>
              </div>

              {selectedProject ? (
                pettyCashExpenses.length === 0 ? (
                  <div className="py-16 text-center text-gray-500 font-medium">No expenses recorded for this project.</div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                          <th className="text-left px-4 py-3">Description</th>
                          <th className="text-right px-4 py-3">Amount</th>
                          <th className="text-left px-4 py-3">Date</th>
                          <th className="px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pettyCashExpenses.map((exp) => (
                          <tr key={exp.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4">
                              <div className="font-bold text-gray-700">{exp.description}</div>
                              <div className="text-[9px] text-gray-400 font-bold uppercase">ID: {exp.id}</div>
                            </td>
                            <td className="px-4 py-4 text-right font-bold text-red-500 tabular-nums">₹{parseFloat(exp.amount).toLocaleString()}</td>
                            <td className="px-4 py-4 text-gray-500 font-medium whitespace-nowrap">
                              {new Date(exp.expense_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex justify-end">
                                <button onClick={() => deletePettyCashExpense(exp.id)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 font-bold border-t border-gray-200">
                        <tr>
                          <td className="px-4 py-4 text-gray-500 text-[10px] uppercase">Total Expenditure</td>
                          <td className="px-4 py-4 text-right text-base text-red-600">
                            ₹{pettyCashExpenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0).toLocaleString()}
                          </td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )
              ) : (
                <div className="py-16 text-center text-gray-500 font-medium bg-white rounded-xl border border-dashed border-gray-200">Select a project to view expenses.</div>
              )}
            </div>
          )}
        </div>
      )
      }
        </>
      )}

      {/* Approve Modal */}
      {approveModalReq && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm border border-emerald-200">
            <div className="flex items-center justify-between p-4 border-b bg-emerald-50/50">
              <h3 className="font-bold text-gray-800 flex items-center gap-2 text-emerald-800"><CheckCircle2 size={18} /> Approve Cash Request from Project</h3>
              <button onClick={() => setApproveModalReq(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={submitApprove} className="p-4 space-y-4">
              <div className="text-center mb-6 mt-2">
                <p className="text-sm text-gray-500 font-medium mb-1">Confirm assigning cash</p>
                <div className="animate-pulse flex items-center justify-center text-3xl font-black text-emerald-600">
                  ₹{Number(editAmount).toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Edit Amount (₹)</label>
                <input type="number" step="0.01" value={editAmount} onChange={e => setEditAmount(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xl font-bold text-gray-700 outline-none focus:border-emerald-500 transition-colors text-center" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setApproveModalReq(null)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving || !editAmount || editAmount <= 0} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">Assign</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModalReq && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm border border-rose-200">
            <div className="flex items-center justify-between p-4 border-b bg-rose-50/50">
              <h3 className="font-bold text-gray-800 flex items-center gap-2 text-rose-800"><XCircle size={18} /> Reject Cash Request from Project</h3>
              <button onClick={() => setRejectModalReq(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={submitReject} className="p-4 space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Rejection Reason *</label>
                <textarea required rows={4} value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 outline-none focus:border-rose-500 transition-colors resize-none" placeholder="Provide a reason for sending this request back..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setRejectModalReq(null)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving || !rejectNotes.trim()} className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-semibold hover:bg-rose-700 disabled:opacity-50">Reject</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRequestModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-gray-800">Cash Request from Project</h3>
              <button onClick={() => setShowRequestModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleRequestSave} className="p-4 space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Project *</label>
                <select required value={requestForm.project_id} onChange={e => setRequestForm(p => ({ ...p, project_id: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 outline-none focus:border-blue-500 bg-white">
                  <option value="">Select Project...</option>
                  {projects.map(pr => <option key={pr.id} value={pr.id}>{pr.project_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Purpose *</label>
                <input required value={requestForm.purpose} onChange={e => setRequestForm(p => ({ ...p, purpose: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 outline-none focus:border-blue-500 transition-colors" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Amount (₹) *</label>
                <input required type="number" value={requestForm.amount} onChange={e => setRequestForm(p => ({ ...p, amount: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-gray-700 outline-none focus:border-blue-500 transition-colors" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowRequestModal(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAllocateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-gray-800">Allocate Petty Cash</h3>
              <button onClick={() => setShowAllocateModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleAllocateSave} className="p-4 space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Project *</label>
                <select required value={allocateForm.project_id} onChange={e => setAllocateForm(p => ({ ...p, project_id: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 outline-none focus:border-blue-500 bg-white">
                  <option value="">Select Project...</option>
                  {projects.map(pr => <option key={pr.id} value={pr.id}>{pr.project_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Amount to Allocate (₹) *</label>
                <input required type="number" min="1" step="0.01" value={allocateForm.assigned_amount} onChange={e => setAllocateForm(p => ({ ...p, assigned_amount: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-gray-700 outline-none focus:border-blue-500 transition-colors" placeholder="e.g. 5000" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Date *</label>
                <input
                  required
                  type="date"
                  value={allocateForm.allocation_date}
                  onChange={e => setAllocateForm(p => ({ ...p, allocation_date: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 outline-none focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Reason *</label>
                <textarea
                  required
                  rows={3}
                  value={allocateForm.reason}
                  onChange={e => setAllocateForm(p => ({ ...p, reason: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 outline-none focus:border-blue-500 transition-colors resize-none"
                  placeholder="Why are you allocating this amount?"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAllocateModal(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">Allocate</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default PettyCashTab;
